/* eslint-disable @typescript-eslint/no-empty-function */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IError } from '../../../../../base/api';
import { QuickApplyStep } from '../../enums';
import { City } from '../../../../../base/interfaces/config';
import { SetFieldParams } from '../../../../../base/interfaces/params';
import { SimilarVacancy, ToggleFavoriteVacancyParams } from '../../../vacancies/api/interfaces';

export interface Fields {
    name: string;
    age: string;
    city: City | null;
    citizenshipCountryId: string;
    description: string;
    smsCode?: string;
}

export interface QuickApplyState {
    vacancyId: string;
    step: QuickApplyStep;
    fields: Fields;
    errors: IError | null;
    shouldScrollToError: boolean;
    similarVacancies: SimilarVacancy[];
}

const fieldsInitialState = {
    name: '',
    age: '',
    city: null,
    citizenshipCountryId: '',
    description: '',
};

const initialState: QuickApplyState = {
    vacancyId: '',
    step: QuickApplyStep.ContactInfo,
    fields: fieldsInitialState,
    errors: null,
    shouldScrollToError: false,
    similarVacancies: [],
};

export const quickApplySlice = createSlice({
    name: 'worker-site__quick-apply',
    initialState,
    reducers: {
        clearData: (state) => {
            state.vacancyId = '';
            state.step = QuickApplyStep.ContactInfo;
            state.fields = fieldsInitialState;
            state.errors = null;
            state.shouldScrollToError = false;
            state.similarVacancies = [];
        },
        getQuickApplyInfo: () => {},
        setQuickApplyInfo: (state, action: PayloadAction<Fields>) => {
            state.fields = action.payload;
        },
        setVacancyId: (state, action: PayloadAction<string>) => {
            state.vacancyId = action.payload;
        },
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        setStep: (state, action: PayloadAction<QuickApplyStep>) => {
            state.step = action.payload;
        },
        setField: (state, action: PayloadAction<SetFieldParams>) => {
            state.fields = {
                ...state.fields,
                [action.payload.id]: action.payload.value,
            };
            if (state.errors === null) {
                return;
            }
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { [action.payload.errorGroupName || action.payload.id]: removedError, ...errors } = state.errors;
            state.errors = errors;
        },
        setErrors: (state, action: PayloadAction<IError>) => {
            if (state.errors) {
                state.errors = {
                    ...state.errors,
                    ...action.payload,
                };
            } else {
                state.errors = action.payload;
            }
        },
        setShouldScrollToError: (state, action: PayloadAction<boolean>) => {
            state.shouldScrollToError = action.payload;
        },
        submit: () => {},
        getSimilarVacancies: {
            reducer: () => {},
            prepare: (vacancyId: string) => ({ payload: vacancyId }),
        },
        setSimilarVacancies: (state, action: PayloadAction<SimilarVacancy[]>) => {
            state.similarVacancies = action.payload;
        },
        similarVacanciesFavoritesToggleRequest: {
            reducer: () => {},
            prepare: (id: number, value: boolean) => ({ payload: { id, value } }),
        },
        similarVacanciesFavoritesToggleSuccess: (state, action: PayloadAction<ToggleFavoriteVacancyParams>) => {
            state.similarVacancies = state.similarVacancies.map((vacancy) => {
                if (vacancy.id === action.payload.id) {
                    return {
                        ...vacancy,
                        isFavorite: action.payload.value,
                    };
                }
                return vacancy;
            });
        },
    },
});

export const quickApplyActions = quickApplySlice.actions;
export const quickApplyReducer = quickApplySlice.reducer;
